import { normName, normalizeCarrier, carriersMatch, namesLooseMatch, significantTokens } from './matchingNormalize';

/** Token-sorted client key (same idea as clientNameKey). */
function saleClientKey(name) {
  if (!name) return '';
  const noAccents = String(name)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return noAccents
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((t) => t.length > 1)
    .sort()
    .join('|');
}

/**
 * Identity of one sale across rolling 90-day production uploads.
 * Same client + carrier + eff date + policy = same sale (not a new enrollment).
 */
export function productionSaleKey(prod) {
  const client = saleClientKey(prod?.client_name);
  const carrier = normalizeCarrier(prod?.carrier);
  const eff = String(prod?.effective_date || '').slice(0, 10);
  const policy = String(prod?.policy_number || prod?.policy_number_production || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  return `${client}|${carrier}|${eff}|${policy}`;
}

function productionRecency(prod) {
  const t = Date.parse(prod?.upload_date || prod?.uploaded_at || '') || 0;
  const id = Number(prod?.id) || 0;
  return t * 1e6 + id;
}

/**
 * Collapse rolling production duplicates to one row per true sale.
 * Keeps the newest upload when the same sale repeats across batches.
 */
export function dedupeProductionSales(rows) {
  const map = new Map();
  for (const p of rows || []) {
    const key = productionSaleKey(p);
    const [client, carrier] = key.split('|');
    if (!client || !carrier) continue;
    const prev = map.get(key);
    if (!prev || productionRecency(p) >= productionRecency(prev)) {
      map.set(key, p);
    }
  }
  return [...map.values()];
}

/**
 * Remittance uploads (BSI→THE) — plain Chargeback on these is house clawback.
 * Agent KR/UHC statements also use Chargeback for agent commission — exclude those.
 */
function isRemittanceUploadName(name) {
  const f = String(name || '')
    .toLowerCase()
    .replace(/[\s()]/g, '_');
  if (!f) return false;
  return (
    /t\.?h\.?e[_.-]*statements?/.test(f) ||
    /the_statements?/.test(f) ||
    /thei_statement_bsi/.test(f) ||
    (/thei_statement/.test(f) && /bsi/.test(f)) ||
    /medicare[_-]?statement/.test(f)
  );
}

/**
 * BSI→THEI override / chargeback rows only.
 * Do NOT include agent New Business / Renewal / agent Chargeback lines —
 * those inflated nets (e.g. Karl Brown −$347 agent CB → false Chase BSI)
 * and exploded lifecycle history.
 */
export function isOverrideStatementRow(row) {
  const c = String(row?.classification || '').toLowerCase();
  if (!c) return false;
  // Agency Override + Agency Override Chargeback
  if (c.includes('agency override')) return true;
  // bare "Override" — not Agent Commission / ACA Agent …
  if (c.includes('override') && !c.includes('agent') && !c.includes('chargeback')) return true;
  // Plain Chargeback: only remittance / agency clawbacks — not agent statement CBs
  if (c.includes('chargeback')) {
    if (c.includes('agency')) return true;
    const upload = row.upload_name || row.upload_original_name || row.original_name || '';
    return isRemittanceUploadName(upload);
  }
  return false;
}

/**
 * Name indexes so findOverrideMatch is O(candidates) instead of O(overrides).
 * Lookup is a superset: exact normName plus any shared significant token.
 * Callers still apply the same namesLooseMatch + carriersMatch predicates.
 */
export function buildNameIndex(rows, nameField = 'client_full_name') {
  const byNormName = new Map();
  const byToken = new Map();
  for (const row of rows || []) {
    const name = row?.[nameField];
    const nn = normName(name).toLowerCase();
    if (nn) {
      const list = byNormName.get(nn);
      if (list) list.push(row);
      else byNormName.set(nn, [row]);
    }
    for (const tok of significantTokens(name)) {
      const list = byToken.get(tok);
      if (list) list.push(row);
      else byToken.set(tok, [row]);
    }
  }
  return {
    byNormName,
    byToken,
    lookup(name) {
      const seen = new Set();
      const out = [];
      const add = (row) => {
        if (seen.has(row)) return;
        seen.add(row);
        out.push(row);
      };
      const nn = normName(name).toLowerCase();
      (byNormName.get(nn) || []).forEach(add);
      for (const tok of significantTokens(name)) {
        (byToken.get(tok) || []).forEach(add);
      }
      return out;
    },
  };
}

/**
 * Match agency production → BSI→THEI override commission rows.
 * Nets ALL matching rows for client+carrier so +$80 override and −$80
 * chargeback → override_net 0 → Missing (not falsely Paid).
 *
 * Optional `index` from buildNameIndex(overrides) — same predicates, fewer scans.
 */
export function findOverrideMatch(production, overrides, index) {
  const prodClient = production.client_name;
  const prodClientNorm = normName(prodClient);
  const prodCarrier = normalizeCarrier(production.carrier);
  if (!prodClientNorm || !prodCarrier) return null;

  const pool = index ? index.lookup(prodClient) : (overrides || []);
  const matches = [];
  for (const override of pool) {
    const overrideClient = override.client_full_name;
    const overrideClientNorm = normName(overrideClient);
    const overrideCarrier = normalizeCarrier(override.carrier);
    const clientMatch =
      prodClientNorm === overrideClientNorm || namesLooseMatch(prodClient, overrideClient);
    if (!clientMatch) continue;
    if (!carriersMatch(prodCarrier, overrideCarrier)) continue;
    matches.push(override);
  }

  if (!matches.length) return null;

  const override_net =
    Math.round(matches.reduce((sum, m) => sum + (parseFloat(m.commission) || 0), 0) * 100) / 100;
  const hasChargeback = matches.some((m) => (parseFloat(m.commission) || 0) < 0);

  return {
    ...matches[0],
    commission: override_net,
    commission_amount: override_net,
    allMatches: matches,
    matchCount: matches.length,
    override_net,
    hasChargeback,
  };
}

/** Paid only when net override dollars remain after chargebacks. */
export function isOverridePaid(overrideMatch) {
  if (!overrideMatch) return false;
  const net =
    overrideMatch.override_net != null
      ? parseFloat(overrideMatch.override_net)
      : parseFloat(overrideMatch.commission || overrideMatch.commission_amount || 0);
  return net > 0;
}

export function holdReasonOf(record) {
  if (!record) return '';
  if (record.hold_reason != null && record.hold_reason !== '') {
    return String(record.hold_reason);
  }
  try {
    const rd =
      typeof record.raw_data === 'string' ? JSON.parse(record.raw_data) : record.raw_data || {};
    return String(rd['Hold Reason'] || '');
  } catch {
    return '';
  }
}

function holdStateOf(record) {
  if (!record) return '';
  if (record.member_state != null && record.member_state !== '') {
    return String(record.member_state);
  }
  try {
    const rd =
      typeof record.raw_data === 'string' ? JSON.parse(record.raw_data) : record.raw_data || {};
    return String(rd['Member State'] || '');
  } catch {
    return '';
  }
}

function holdCountyOf(record) {
  if (!record) return '';
  if (record.member_county != null && record.member_county !== '') {
    return String(record.member_county);
  }
  try {
    const rd =
      typeof record.raw_data === 'string' ? JSON.parse(record.raw_data) : record.raw_data || {};
    return String(rd['Member County'] || '');
  } catch {
    return '';
  }
}

function isLicensingHoldRecord(record) {
  if (!record || record.classification !== 'Held') return false;
  const reason = holdReasonOf(record).toLowerCase();
  return reason.includes('not licensed') || reason.includes('not appointed');
}

/** Held-reason pill for Override Recon. Prefers slim extracted fields over raw_data. */
export function getHoldDetail(m) {
  const src = (m?.carrierBSI && m.carrierBSI.classification === 'Held') ? m.carrierBSI
            : m?.heldRecord || null;
  if (!src) return null;
  const reason = holdReasonOf(src) || null;
  const state = holdStateOf(src) || null;
  const county = holdCountyOf(src) || null;
  if (!reason && !state && !county) return null;
  return { reason, state, county };
}

/**
 * Three-way status for Hector production → Carrier→BSI → BSI→THEI.
 *
 *   not_paid_to_bsi — on Hector, carrier BSI file uploaded, sale NOT on it
 *   chase_bsi       — Carrier→BSI has $, and we have NO house remittance /
 *                     Agency Override history at all (true unpaid remittance)
 *                   — NOT when override net is $0 / negative after clawbacks
 *                     (those are returnee / settled, not a chase)
 *   pending         — carrier BSI statement not uploaded yet for this carrier
 */
export function getThreeWayOverrideStatus(m) {
  if (!m) return 'pending';
  if (m.lifecycle === 'paid') return 'paid';
  if (m.lifecycle === 'chargeback') return 'chargeback';
  if (m.production?.manual_override_status) return m.production.manual_override_status;
  if (isOverridePaid(m.override)) return 'paid';

  const prodStatus = String(m.production?.status || '')
    .toUpperCase()
    .trim();
  if (['WITHDRAWN', 'IN PROGRESS', 'CANCELLED', 'DENIED'].includes(prodStatus)) {
    return 'no_pay_expected';
  }

  const carrierAmt = m.carrierBSI ? parseFloat(m.carrierBSI.commission || 0) : null;
  // True Chase: carrier paid BSI, THEI never got a remittance/override line.
  // Net $0 / negative after clawbacks is NOT chase (Karl-style bogus report).
  if (m.carrierBSI && carrierAmt > 0 && !m.override) return 'chase_bsi';
  if (m.carrierBSI && carrierAmt === 0) {
    if (isLicensingHoldRecord(m.carrierBSI)) return 'held_licensing';
    return 'request_audit';
  }
  if (!m.carrierBSI && m.heldRecord && isLicensingHoldRecord(m.heldRecord)) {
    return 'held_licensing';
  }
  // Hector sale missing from uploaded carrier BSI statements → BSI wasn't paid.
  if (!m.carrierBSI && m.carrierUploaded) return 'not_paid_to_bsi';
  return 'pending';
}

/**
 * Tab bucket for Agency Override Recon.
 * Auto chase_bsi / not_paid_to_bsi must not all dump into Missing.
 */
export function getOverrideReconCategory(m) {
  if (!m) return 'missing';
  const manual = m.production?.manual_override_status || null;
  if (manual === 'no_pay_expected') return 'cancelled';
  if (manual === 'paid') return 'paid';
  // Manual chase / not-on-BSI still live under Missing (status badge shows which).
  if (manual === 'chase_bsi' || manual === 'not_paid_to_bsi') return 'missing';

  if (m.lifecycle === 'chargeback' || m.categoryHint === 'cancelled') return 'cancelled';
  if (m.lifecycle === 'paid' || m.categoryHint === 'paid') return 'paid';
  if (m.categoryHint && m.categoryHint !== 'missing') return m.categoryHint;

  if (isOverridePaid(m.override)) return 'paid';

  const prodStatus = String(m.production?.status || '')
    .toUpperCase()
    .trim();
  if (['WITHDRAWN', 'IN PROGRESS', 'CANCELLED', 'DENIED'].includes(prodStatus)) {
    return 'cancelled';
  }

  const status = String(m.production?.status || '').toLowerCase();
  if (status.includes('plan denied') || status.includes('plan_denied') || status.includes('denied')) {
    return 'plandenied';
  }
  if (status.includes('plan change') || status.includes('plan_change')) return 'planchange';
  if (status.includes('cancel') || status.includes('terminated')) return 'cancelled';

  // chase_bsi / not_paid_to_bsi / pending → Missing tab; badge shows the tag.
  return 'missing';
}

function wrapSingleOverride(row) {
  const amt = Math.round((parseFloat(row.commission) || 0) * 100) / 100;
  return {
    ...row,
    commission: amt,
    commission_amount: amt,
    allMatches: [row],
    matchCount: 1,
    override_net: amt,
    hasChargeback: amt < 0,
  };
}

/**
 * Expand one production row into lifecycle history rows so recon buckets
 * show the full story: Paid (+override), Cancelled (chargeback/left),
 * and Missing again when she returns with no open override.
 */
export function expandOverrideLifecycle(production, overrides, index) {
  const prodId = production?.id != null ? String(production.id) : 'unknown';
  // Never expand agent NB/renewal lines into history — override/chargeback only.
  // When an index is supplied, the caller already filtered to override rows.
  const overrideOnly = index ? (overrides || []) : (overrides || []).filter(isOverrideStatementRow);
  const bundled = findOverrideMatch(production, overrideOnly, index);

  if (!bundled) {
    return [
      {
        production,
        override: null,
        lifecycle: 'missing',
        categoryHint: 'missing',
        rowKey: `prod-${prodId}-missing`,
        isHistory: false,
      },
    ];
  }

  const rows = [];
  const all = bundled.allMatches || [];

  all.forEach((row, idx) => {
    const amt = parseFloat(row.commission) || 0;
    const idPart = row.id != null ? String(row.id) : String(idx);
    if (amt > 0) {
      rows.push({
        production,
        override: wrapSingleOverride(row),
        lifecycle: 'paid',
        categoryHint: 'paid',
        rowKey: `prod-${prodId}-paid-${idPart}`,
        isHistory: true,
      });
    } else if (amt < 0) {
      rows.push({
        production,
        override: wrapSingleOverride(row),
        lifecycle: 'chargeback',
        categoryHint: 'cancelled',
        rowKey: `prod-${prodId}-chargeback-${idPart}`,
        isHistory: true,
      });
    }
  });

  // Return / clawed-back: still in production but no open paid override.
  if (!isOverridePaid(bundled)) {
    rows.push({
      production,
      override: bundled,
      lifecycle: 'missing',
      categoryHint: 'missing',
      rowKey: `prod-${prodId}-missing`,
      isHistory: false,
    });
  }

  return rows;
}

/**
 * Aetna returnee-safe active check.
 * If Enroll_Status is Active / Future Active, keep even when Exit/Term still
 * says Voluntary from a prior disenrollment (client came back to the plan).
 */
/**
 * Period-scoped Carrier→BSI match with a period-agnostic fallback — same lookup
 * AgencyProductionRecon.js used inline before this was factored out for reuse
 * (e.g. by All Data's unpaid-production search, which needs the identical rule).
 */
function _findCarrierBSIMatch(prod, carrierRecords, index) {
  const prodPeriod = prod.payment_period || '';
  if (prodPeriod) {
    const pool = index ? index.lookup(prod.client_name) : carrierRecords;
    const samePeriod = pool.filter((r) => r.payment_period === prodPeriod);
    const periodMatch = findOverrideMatch(prod, samePeriod);
    if (periodMatch) return periodMatch;
  }
  // Fall back across periods — Hector month ≠ statement month still means carrier paid BSI.
  return findOverrideMatch(prod, carrierRecords, index);
}

/** Period-agnostic lookup for a Held record by client+carrier. */
function _findHeldRecord(prod, carrierRecords, index) {
  const prodClientNorm = normName(prod.client_name);
  const prodCarrier = normalizeCarrier(prod.carrier);
  const pool = index
    ? (index.byNormName.get(prodClientNorm.toLowerCase()) || [])
    : (carrierRecords || []);
  return pool.find(r =>
    r.classification === 'Held' &&
    normName(r.client_full_name) === prodClientNorm &&
    carriersMatch(r.carrier, prodCarrier)
  ) || null;
}

/**
 * Build the same per-sale match rows AgencyProductionRecon.js renders (one row per
 * paid/chargeback/missing lifecycle entry), from the same four inputs that page loads.
 * Factored out of that page's `matches` useMemo (byte-identical logic) so any other
 * surface — All Data's unpaid-production search — can compute the exact same status
 * for a sale instead of re-deriving payment rules of its own.
 */
export function buildOverrideMatches(production, overrides, carrierBSIRecords, bsiUploadedKeys) {
  const bsiKeysList = [...bsiUploadedKeys];
  const rows = [];
  const overrideRows = (overrides || []).filter(isOverrideStatementRow);
  const overrideIndex = buildNameIndex(overrideRows, 'client_full_name');
  const bsiIndex = buildNameIndex(carrierBSIRecords, 'client_full_name');
  // Rolling 90-day production repeats the same sale — one recon row per true sale.
  const uniqueProduction = dedupeProductionSales(production);
  for (const prod of uniqueProduction) {
    const carrierBSI = _findCarrierBSIMatch(prod, carrierBSIRecords, bsiIndex);
    const heldRecord = _findHeldRecord(prod, carrierBSIRecords, bsiIndex);
    const prodCarrier = normalizeCarrier(prod.carrier || '');
    const prodPeriod = prod.payment_period || String(prod.effective_date || '').substring(0, 7).replace('-', '') || '';
    const carrierUploaded = bsiUploadedKeys.has(`${prodCarrier}|${prodPeriod}`) ||
      bsiKeysList.some((k) => k.startsWith(`${prodCarrier}|`));
    const lifecycleRows = expandOverrideLifecycle(prod, overrideRows, overrideIndex);
    for (const life of lifecycleRows) {
      rows.push({
        ...life,
        carrierBSI: life.isHistory ? null : carrierBSI,
        heldRecord: life.isHistory ? null : heldRecord,
        carrierUploaded: life.isHistory ? false : carrierUploaded,
      });
    }
  }
  return rows;
}

export function isAetnaActivePolicy(row) {
  const str = (v) => String(v == null ? '' : v);
  const enrollStatus = str(row.Enroll_Status || row.enroll_status || row.status).trim().toUpperCase();
  const exitStatus = str(row.Exit_Status || row.exit_status).trim().toUpperCase();
  const termStatus = str(row.Term_Status || row.term_status).trim().toUpperCase();

  // Current enroll wins over stale exit/term from a prior leave (returnees).
  if (enrollStatus.includes('CANCEL')) return false;
  if (enrollStatus.includes('ACTIVE') || enrollStatus.includes('FUTURE')) return true;
  if (exitStatus.includes('VOLUNTARY') || exitStatus.includes('CANCEL')) return false;
  if (termStatus.includes('VOLUNTARY') || termStatus.includes('CANCEL')) return false;
  return false;
}
